import { BUILDINGS, WALL_PIECES } from './buildings';
import { CIVS, UNIQUE_TECHS } from './civilisations';
import { RESOURCES } from './resources';
import { TECHS } from './technologies';
import type { BuildingDef, CivDef, ResourceDef, TechDef, UnitDef } from './types';
import { LATER_UNITS, UNITS } from './units';

export const enum Cat {
  Unit = 0,
  Building = 1,
  Resource = 2,
  /** A unit inside a building or ship (garrisoned or carried): off the map until it leaves. */
  Held = 3,
}

export interface KindInfo {
  index: number;
  key: string;
  cat: Cat;
  unit?: UnitDef;
  building?: BuildingDef;
  resource?: ResourceDef;
}

/** Every entity kind gets a stable integer index used by the simulation. */
export const KINDS: KindInfo[] = [];
const kindByKey = new Map<string, KindInfo>();

function register(info: Omit<KindInfo, 'index'>): void {
  if (kindByKey.has(info.key)) throw new Error(`Duplicate kind ${info.key}`);
  const k: KindInfo = { ...info, index: KINDS.length };
  KINDS.push(k);
  kindByKey.set(k.key, k);
}

for (const u of UNITS) if (!LATER_UNITS.includes(u)) register({ key: u.key, cat: Cat.Unit, unit: u });
for (const b of BUILDINGS) register({ key: b.key, cat: Cat.Building, building: b });
for (const r of RESOURCES) register({ key: r.key, cat: Cat.Resource, resource: r });
for (const b of WALL_PIECES) register({ key: b.key, cat: Cat.Building, building: b });
// Added since release: numbered after everything above so saved games keep their kind numbers.
for (const u of LATER_UNITS) register({ key: u.key, cat: Cat.Unit, unit: u });

export function kind(key: string): KindInfo {
  const k = kindByKey.get(key);
  if (!k) throw new Error(`Unknown kind ${key}`);
  return k;
}
export const kindIndex = (key: string): number => kind(key).index;
export const hasKind = (key: string): boolean => kindByKey.has(key);

export const ALL_TECHS: TechDef[] = [...TECHS, ...UNIQUE_TECHS];
const techByKey = new Map(ALL_TECHS.map((t, i) => [t.key, i] as const));
export function techIndex(key: string): number {
  const i = techByKey.get(key);
  if (i === undefined) throw new Error(`Unknown tech ${key}`);
  return i;
}

const civByKey = new Map(CIVS.map((c, i) => [c.key, i] as const));
export function civIndex(key: string): number {
  const i = civByKey.get(key);
  if (i === undefined) throw new Error(`Unknown civ ${key}`);
  return i;
}
export const civDef = (i: number): CivDef => CIVS[i];

/** Validate cross references at module load so data mistakes fail fast. */
function validate(): void {
  for (const b of BUILDINGS) {
    for (const t of b.trains ?? []) if (!kindByKey.has(t)) throw new Error(`${b.key} trains unknown ${t}`);
    for (const r of b.researches ?? []) if (!techByKey.has(r)) throw new Error(`${b.key} researches unknown ${r}`);
  }
  for (const u of UNITS) if (!kindByKey.has(u.trainedAt)) throw new Error(`${u.key} trainedAt unknown ${u.trainedAt}`);
  for (const t of ALL_TECHS) {
    if (!kindByKey.has(t.researchedAt)) throw new Error(`${t.key} researchedAt unknown ${t.researchedAt}`);
    for (const r of t.requires ?? []) if (!techByKey.has(r)) throw new Error(`${t.key} requires unknown ${r}`);
  }
  for (const c of CIVS) {
    for (const u of c.uniqueUnits) if (!kindByKey.has(u)) throw new Error(`${c.key} unique unit ${u}`);
    for (const t of c.uniqueTechs) if (!techByKey.has(t)) throw new Error(`${c.key} unique tech ${t}`);
    for (const d of c.disabled) if (!kindByKey.has(d)) throw new Error(`${c.key} disables unknown ${d}`);
  }
}
validate();
